import { colors } from '@apollo/brand';
import { customColors, customColorsRaw } from '@apollo/orbit';
import classnames from 'classnames';
import { sum } from 'lodash';
import React, { Fragment } from 'react';

import { Cursor } from 'src/components/charts/helpers/Cursor';

export interface BarData<XValue> {
  stackCounts: { value: number }[];
  y: number;
  x: XValue;
}

export interface BarColor {
  fill?: string;
  marker?: string;
  cursor?: string;
  muted?: string;
}

const defaultColors: Readonly<BarColor>[] = [
  {
    fill: `rgb(${customColorsRaw.flowchart.d} / 0.6)`,
    cursor: customColors.flowchart.d,
    marker: `rgb(${customColorsRaw.flowchart.c} / 0.6)`,
  },
  {
    fill: `${colors.primitives.yellow[300]}99`,
    cursor: colors.primitives.yellow[300],
    marker: colors.primitives.yellow[300],
  },
];

export function Bars<Value, Data extends BarData<Value>>({
  height,
  width,
  xScale,
  yScale,
  data,
  cursorValueIndex,
  cursorStackIndex,
  onClick,
  marker,
  barColors = defaultColors,
  barWidthPercent = 0.8,
  getTooltip,
  hideCursorArrow = false,
}: {
  height: number;
  width: number;
  xScale: ((value: Value) => number) & { range(): number[] };
  yScale: d3.ScaleLinear<number, number>;
  data: Array<Data>;
  cursorValueIndex: number | null;
  cursorStackIndex?: number | null;
  onClick?: (data: Data, cursorStackIndex: number | null) => void;
  marker?: Value;
  barColors?: Readonly<Array<BarColor>>;
  barWidthPercent?: number;
  getTooltip: (data: Data, stackIndex: number) => React.ReactNode;
  hideCursorArrow?: boolean;
}) {
  const barWidth = width / data.length;
  const barRectWidth = Math.min(
    Math.max(barWidth * barWidthPercent, 1),
    barWidth,
  );

  const cursorValue =
    typeof cursorValueIndex === 'number' ? data[cursorValueIndex] : undefined;
  return (
    <>
      {data.map((bar, i) => {
        const barXTransform = xScale(bar.x) - barRectWidth / 2;

        return (
          <Fragment key={`${bar.x}-${bar.y}`}>
            <rect
              onClick={() => onClick?.(bar, null)}
              width={barRectWidth}
              height={height}
              fill="transparent"
              className={classnames({ cursor: 'default' })}
              transform={`translate(${barXTransform}, 0)`}
            />
            {bar.stackCounts
              .map(({ value }) => {
                // Ensure a minimum height for zero values
                const barHeight = height - yScale(value);
                return value > 0 ? Math.max(barHeight, 2) : 0;
              })
              .map((barHeight, stackIndex, arr) => {
                const { radius, inset } = arr
                  .slice(stackIndex + 1)
                  .every((h) => h === 0)
                  ? {
                      radius: Math.min(2, barHeight),
                      inset: Math.min(0.89543, barHeight),
                    }
                  : { radius: 0, inset: 0 };
                const palletteIdx = stackIndex % barColors.length;
                // Allow the bar colors to repeat if passing fewer colors than
                // the number of stacks.
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const palette = barColors[palletteIdx]!;

                // Compute the fill color based on cursor, marker, stack.
                // If the marker is set, it takes precedent, otherwise if
                // there is an active cursor value or stack value, highlight it.
                // Fallback to the fill value otherwise.
                const hasCursorValue = cursorValueIndex !== null;
                const hasStackValue = (cursorStackIndex ?? null) !== null;
                const showMarker = marker === bar.x;

                // If there is an active stack, use the stack highlighting
                // behavior, which highlights only the active stack and
                // mutes the other stacks. Otherwise use full bar highlighting.
                const showCursor =
                  (hasCursorValue &&
                    !hasStackValue &&
                    cursorValueIndex === i) ||
                  (hasStackValue && cursorStackIndex === stackIndex);
                const fill = showMarker
                  ? palette.marker
                  : showCursor
                    ? palette.cursor
                    : hasCursorValue && palette.muted
                      ? palette.muted
                      : palette.fill;

                return (
                  <Cursor
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${stackIndex}-${bar.x}`}
                    width={width}
                    cursorValue={
                      cursorValue
                        ? {
                            ...cursorValue,
                            tooltipContent: getTooltip(bar, stackIndex),
                            x: xScale(cursorValue.x),
                            y: barHeight,
                          }
                        : undefined
                    }
                    showArrow={!hideCursorArrow}
                  >
                    <path
                      // eslint-disable-next-line react/no-array-index-key
                      key={String(stackIndex)}
                      className={classnames({ 'cursor-pointer': !!onClick })}
                      onClick={() => onClick?.(bar, stackIndex)}
                      d={`M0 ${radius}C0 ${inset} ${inset} 0 ${radius} 0H${
                        barRectWidth - inset - inset
                      }C${
                        barRectWidth - inset
                      } 0 ${barRectWidth} ${inset} ${barRectWidth} ${radius}V${barHeight}H0V${radius}Z`}
                      transform={`translate(${barXTransform}, ${
                        height - sum(arr.slice(0, stackIndex + 1))
                      })`}
                      fill={fill}
                    />
                  </Cursor>
                );
              })}
          </Fragment>
        );
      })}
    </>
  );
}
